import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@cmg/design-system';
import { FormikSelectField } from '@cmg/design-system-formik';
import { useFormikContext } from 'formik';
import { useCallback } from 'react';

import { useConvertibleFilingTalkRanges } from '../../../../../../../../common/hooks/useConvertibleFilingTalkRanges';
import MandatoryAsterisk from '../../../../../../../../design-system/components/data-display/mandatory-asterisk/MandatoryAsterisk';
import { InterestLevelInterestUnit, SecurityType } from '../../../../../../../../graphql';
import { SyndicateIndication_PublishedOfferings_Terms_FilingsPartsFragment } from '../../../../context/hooks/syndicate/__generated__';
import { IndicationFormValues, InterestLevelValue } from '../utils/IndicationSectionValidation';
import { getConvertibleDemandUnitOptions } from './IndicationSectionForm.model';
import { InterestLevelRowFormConvertibles } from './InterestLevelRowFormConvertibles';

export type InterestLevelTableFormConvertiblesProps = Readonly<{
  interestLevels: InterestLevelValue[];
  filings: Readonly<SyndicateIndication_PublishedOfferings_Terms_FilingsPartsFragment[]> | null;
  currencyCode: string;
  securityType: SecurityType | null;
  onRemove: (index: number) => void;
  showPercentageOfPar: boolean;
}>;

export function InterestLevelTableFormConvertibles({
  interestLevels,
  filings,
  currencyCode,
  onRemove,
  showPercentageOfPar,
  securityType,
}: InterestLevelTableFormConvertiblesProps) {
  const { setFieldValue } = useFormikContext<IndicationFormValues>();

  const convertibleFilingTalkRanges = useConvertibleFilingTalkRanges(filings);

  const handleChangeInterestUnit = useCallback(
    (newInterestUnit: InterestLevelInterestUnit) => {
      setFieldValue('selectedInterestUnit', newInterestUnit);
      setFieldValue(
        'interestLevels',
        interestLevels.map(level => ({ ...level, interestUnit: newInterestUnit }))
      );
    },
    [interestLevels, setFieldValue]
  );

  return (
    <Stack gap={1}>
      <FormikSelectField
        select
        label="Demand Unit"
        showHelperTextInTooltip
        name="selectedInterestUnit"
        sx={{ width: 187 }}
        onChange={handleChangeInterestUnit}
        options={getConvertibleDemandUnitOptions(securityType)}
      />
      <TableContainer>
        <Table sx={{ width: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell width={150} align="right">
                Demand Quantity <MandatoryAsterisk />
              </TableCell>
              <TableCell width={130}>
                Order Type <MandatoryAsterisk />
              </TableCell>
              <TableCell align="right">Coupon</TableCell>
              <TableCell align="right">Premium</TableCell>
              <TableCell width={105} align="right">
                % of Par
              </TableCell>
              <TableCell width={34} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {interestLevels.map((interestLevel, index) => (
              <InterestLevelRowFormConvertibles
                key={index}
                index={index}
                interestLevel={interestLevel}
                currencyCode={currencyCode}
                onRemove={onRemove}
                securityType={securityType}
                showPercentageOfPar={showPercentageOfPar}
                convertibleFilingTalkRanges={convertibleFilingTalkRanges}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {showPercentageOfPar && (
        <Typography variant="body2">
          <Typography component="span" fontWeight="bold">
            Note:
          </Typography>{' '}
          This offering allows % of Par.
        </Typography>
      )}
    </Stack>
  );
}
